/**
 * Handles fetching product data for multiple products based on part numbers.
 */
import { useDefaultStore } from '@/data/Content/DefaultStore';
import {
	fetcherForProducts,
	getProductsSWRKey,
	ProductFetchType,
} from '@/data/Content/Products-Server';
// import {
// 	fetcher,
// 	getProducts,
// 	getProductsByKeyType
// } from '@/data/Content/Products-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { PRODUCT_DATA_KEY } from '@/data/Content/_Product';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { TYPES } from '@/data/constants/product';
import { HDM_URLS_PAGE_PATHS } from '@/data/constants/urlPathConstants';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { expand, shrink } from '@/data/utils/keyUtil';
import { currencyFallbackMiddleWare } from '@/data/utils/swr/currencyFallbackMiddleWare';
import { isBuyableProduct } from 'presentation/events/utils/productUtils';
import { useMemo } from 'react';
import useSWR from 'swr';
// export { getProducts, getProductsByKeyType };

const DATA_KEY = PRODUCT_DATA_KEY;

// Maybe need to revisit and use id instead for consistency of cache and fallback
type Props = {
	id?: string[];
	isCEId?: boolean;
	condition?: boolean;
	contractId?: string | string[];
	profileName?: string;
	excludeProducts?: boolean;
	isWishList?: boolean;
};

const validation = (storeId: string, id: string[], condition: boolean, physicalStoreId: string) =>
	storeId && id && id.length > 0 && condition && physicalStoreId;
export const useProducts = ({
	id = [],
	isCEId = false,
	condition = true,
	contractId,
	profileName,
	excludeProducts = false,
	isWishList = false,
}: Props) => {
	const params = useExtraRequestParameters();
	const router = useNextRouter();
	const { settings } = useSettings();
	const { user } = useUser();
	const currentContract = getContractIdParamFromContext(user?.context);
	const contract = contractId ? { contractId } : currentContract;
	const {
		storeId,
		defaultCatalogId: catalogId,
		langId,
		defaultCurrency,
	} = getClientSideCommon(settings, router);
	// Same store pair the PDP sends with `HCL_V2_findProductByPartNumber_Details` (see
	// `useProduct`): `physicalStoreId` is the active store code and `stLocId` is that
	// store's uniqueId.
	const { physicalStoreId, uniqueId: stLocId, storeData } = useDefaultStore();
	const marketId = storeData?.marketId ?? '';

	const lookupKey = isCEId ? 'id' : 'partNumber';
	const cacheKey = getProductsSWRKey({
		storeId,
		lookupKey,
		lookupValue: id,
		catalogId,
		langId,
		physicalStoreId,
		profileName,
		marketId,
		stLocId,
		...contract,
		...getCurrencyParamFromContext(user?.context),
	});

	const { data, error, isLoading, isValidating } = useSWR(
		validation(storeId, id, condition, physicalStoreId) ? [shrink(cacheKey), DATA_KEY] : null,
		async ([props]) => fetcherForProducts(true)(expand(props), params),
		{ use: [currencyFallbackMiddleWare({ defaultCurrency })] }
	);

	const products = useMemo(() => {
		if (!data) return [];

		return data
			.map((d) => d.product)
			.filter((p) => {
				if (!p) return false;

				if (
					!isWishList &&
					excludeProducts &&
					!isBuyableProduct({
						productAttributes: p.attributes,
						physicalStoreId,
					})
				) {
					return false;
				}
				const isValidBundle = p.type === TYPES.bundle && (p?.components?.length ?? 0) >= 2;
				const isValidProduct =
					p.type !== TYPES.bundle &&
					p?.productPrice?.offer !== 0 &&
					p?.seo?.href?.includes(HDM_URLS_PAGE_PATHS.ItemPage);

				return isValidBundle || isValidProduct;
			});
	}, [data, excludeProducts, physicalStoreId, isWishList]);

	const productIds = useMemo(() => products?.map((p) => p && p.id), [products]);

	return {
		rawData: data as ProductFetchType[] | undefined,
		products: products ?? undefined,
		productIds: productIds ?? undefined,
		loading: !!id && !error && isLoading && condition,
		isValidating, // This is used to determine whether to show the spinner when the product is being fetched
		error,
	};
};
